import { UserFocus } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { setIsPasswordChanged } from 'theSource/features/ChangePassword';
import {
  ErrorResponseDto,
  ResponseDto,
  SubscriberEditRequestDto,
} from 'theSource/types';
import { Logo } from 'theSource/components/Logo';
import {
  addSnackbar,
  Form,
  IconButton,
  Link,
  selectSnackbar,
  Spacer,
  TextField,
} from 'theSource/components';
import { getCompanyName, useApiMutation, mapOptions } from 'theSource/utils';
import {
  BackgroundImageContainerStyled,
  LoginPageWrapperStyled,
  LogoWrapperStyled,
  PaperStyled,
} from './LoginStyled';
import {
  selectRedirectLink,
  setAuthenticated,
  setRedirectLink,
  setMfaEnabled,
  setMfaRequired,
} from './services';
import {
  LoginErrorDto,
  LoginFormValues,
  LoginRequestDto,
  LoginResponseDto,
} from './types';
import {
  gerErrorSnackbar,
  INITIAL_VALUES,
  LOGIN_QUERY_KEY,
  UPDATE_PASSWORD_ERROR_CODE,
  useNativeAuth,
  ACCEPTED_VALUES,
  getLocalLanguage,
} from './utils';
import {
  GET_COMPANY_MANIFEST_QUERY_KEY,
  GET_CONTENT_SETTINGS_QUERY_KEY,
  useConfigurationQuery,
  GET_SUBSCRIBER_QUERY_KEY,
  UPDATE_SUBSCRIBER_QUERY_KEY,
  useCompanySettingsQuery,
} from 'theSource/queries';
import { CompanyManifestResponseDto } from 'theSource/queries/useCompanyManifestQuery/types';
import { selectNativeAuth } from 'theSource/services';
import { ROUTES } from 'theSource/app/routes';
import { setIsAdmin } from 'theSource/features/AccessDenied';
import { SUBSCRIBER_LOOKUP_CODE } from 'theSource/features/AccessDenied/utils';
import { setLogoutClicked } from '../../../features/HomePage/services';
import { setEnrollmentContext } from '../Home/components/EnrollmentBanner/services';
import { ChatBot } from 'components/ChatBot';
import { PsSelectTuner } from 'stencilSetup';
import './LoginPageStyled.css';

export const Login = () => {
  const { configuration } = useConfigurationQuery();
  const redirectLink = useSelector(selectRedirectLink);
  const { isAutomaticAuthDisabled } = useSelector(selectNativeAuth);
  const snackbar = useSelector(selectSnackbar);
  const queryClient = useQueryClient();
  const manifestData: CompanyManifestResponseDto = queryClient.getQueryData(
    GET_COMPANY_MANIFEST_QUERY_KEY
  )!;

  // would rather use the redux value but the login test file was not agreeing
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const { t, i18n } = useTranslation('pages.login');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    isNativeAuthFlowEnabled,
    isNativeAuthInProgress,
    isNativeAuthCompleted,
    authenticate,
  } = useNativeAuth();

  const [externalError, setExternalError] = useState(false);
  const [hasLoadedImage, setHasLoadedImage] = useState(false);
  const [hasLoadedLogo, setHasLoadedLogo] = useState(false);

  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowLoginPassword(!showLoginPassword);
  };

  const validationSchema: yup.SchemaOf<LoginFormValues> = useMemo(
    () =>
      yup.object({
        username: yup.string().required(t('messages.login:usernameRequired')),
        password: yup.string().required(t('messages.login:passwordRequired')),
        app_url: yup.string(),
      }),
    [t]
  );

  const [prefLanguage, setprefLanguage] = useState(getLocalLanguage() || '');

  const setLocalLanguage = (desiredLanguage: string) => {
    localStorage.setItem('preferred_language', desiredLanguage);
  };

  const { mutate: updateSubscriber } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    SubscriberEditRequestDto
  >({
    queryKey: UPDATE_SUBSCRIBER_QUERY_KEY,
    queryKeysToInvalidate: GET_SUBSCRIBER_QUERY_KEY,
    method: 'put',
    endpoint: '/subscriber',
    getErrorMessage: (error) => error.response?.data?.details,
  });

  const handleNavigation = useCallback(() => {
    if (companySettings && !companySettings.data.source_license) {
      navigate(ROUTES.enrollment.path);
    } else {
      navigate(redirectLink || '/');
    }
  }, [navigate, redirectLink, companySettings]);

  const handleSuccessfulAuthentication = useCallback(() => {
    dispatch(setAuthenticated(true));
    dispatch(setIsAdmin(false));
    dispatch(setEnrollmentContext(''));
    handleNavigation();
    dispatch(
      setRedirectLink(
        companySettings && !companySettings?.data.source_license
          ? '/enrollment'
          : null
      )
    );
    if (snackbar.isOpen && snackbar.severity === 'info') {
      // If user doesn't dismiss the ios download instructions snackbar and logs in, we dispatch the same snackbar so it would have autoHide and it wouldn't block bottom navigation
      dispatch(
        addSnackbar({
          ...snackbar,
          position: 'aboveMenu',
          disableAutoHide: false,
        })
      );
    }
    // save the language if changed
    if (
      prefLanguage &&
      prefLanguage !== getLocalLanguage() &&
      ACCEPTED_VALUES.includes(prefLanguage)
    ) {
      updateSubscriber({
        subscriber: { preferred_locale: prefLanguage },
      });
      // cache the language
      setLocalLanguage(prefLanguage);
    }
  }, [
    dispatch,
    updateSubscriber,
    handleNavigation,
    snackbar,
    prefLanguage,
    companySettings,
  ]);

  const { isLoading: isLoggingIn, mutate: login } = useApiMutation<
    LoginResponseDto,
    LoginErrorDto,
    LoginRequestDto
  >({
    queryKey: LOGIN_QUERY_KEY,
    method: 'put',
    endpoint: '/login',
    queryKeysToInvalidate: GET_CONTENT_SETTINGS_QUERY_KEY,
    onSuccess: (data) => {
      if (data.auth_result === 'mfa_required') {
        window.location.assign(
          `${configuration?.apiHost}/authentication/mfa/contact_method`
        );
        return; // return here to prevent further code execution as we navigate to MFA authentication
      } else if (data.auth_result === 'successful_mfa_configure_required') {
        dispatch(setAuthenticated(false));
        dispatch(setMfaEnabled(true));
        dispatch(setMfaRequired(true));
        navigate(ROUTES.additional.account.securityPreferences.path, {
          state: { fromLogin: true },
        });
        return;
      } else if (data.user_code === SUBSCRIBER_LOOKUP_CODE) {
        // the user is a subscriber, so we want to go through normal auth
        handleSuccessfulAuthentication();
      } else {
        // the user is not a subscriber, so we need to force direct them to the admin page
        dispatch(setIsAdmin(true));
        dispatch(setAuthenticated(false));
        navigate(ROUTES.public.accessDenied.path);
      }
    },
    onError: (error) => {
      if (error.response?.data?.error_code === UPDATE_PASSWORD_ERROR_CODE) {
        navigate(ROUTES.public.changePassword.path);
        dispatch(setIsPasswordChanged(false));
      } else {
        setExternalError(true);
        dispatch(addSnackbar(gerErrorSnackbar(t)));
      }
    },
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
    snackbarOptions: {
      position: 'bottom',
    },
  });

  const handleSubmit = (values: LoginFormValues) => {
    setExternalError(false);
    login({
      user_name: values.username,
      password: values.password,
      app_url: getCompanyName(),
      the_source_login: true,
    });
  };

  useEffect(() => {
    // Call to device authenticator freezes UI, thus waiting for content to be loaded before starting the automatic flow
    if (
      isNativeAuthFlowEnabled &&
      !isAutomaticAuthDisabled &&
      hasLoadedImage &&
      hasLoadedLogo
    ) {
      authenticate();
    }
  }, [
    hasLoadedImage,
    hasLoadedLogo,
    isAutomaticAuthDisabled,
    authenticate,
    isNativeAuthFlowEnabled,
  ]);

  useEffect(() => {
    if (isNativeAuthCompleted) {
      handleSuccessfulAuthentication();
    }
  }, [isNativeAuthCompleted, handleSuccessfulAuthentication]);

  useEffect(() => {
    // deleting chatbot history and session_id from localstorage
    // once user navigates to login page
    localStorage.removeItem('session_id');
    localStorage.removeItem('chatbot_messages');
    dispatch(setLogoutClicked(false));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (prefLanguage) {
      i18n.changeLanguage(prefLanguage);
    }
  }, [i18n, prefLanguage]);

  const handleOnImageLoad = () => setHasLoadedImage(true);
  const handleOnLogoLoad = () => setHasLoadedLogo(true);
  const isMobile = useMediaQuery('(max-width: 600px)');

  const options =
    manifestData?.enabled_languages &&
    manifestData?.enabled_languages.length > 1
      ? mapOptions(manifestData.enabled_languages)
      : [];

  const language = t('language');

  return (
    <LoginPageWrapperStyled data-testid="login-page" className="login-page">
      <BackgroundImageContainerStyled
        data-testid="background-image"
        src={manifestData?.background_image}
        onLoad={handleOnImageLoad}
        alt="" // w3c decorative img handling
      />

      <PaperStyled>
        <Spacer height={7} />
        <span aria-hidden="true" role="presentation">
          <LogoWrapperStyled>
            <Logo onLoad={handleOnLogoLoad} />
          </LogoWrapperStyled>
        </span>
        <Spacer height={7} />
        <main id="mainContent" tabIndex={-1}>
          <h1 className="sr-only">{t('title')}</h1>
          <Form<LoginFormValues>
            validationSchema={validationSchema}
            isLoading={isLoggingIn}
            onSubmitClick={handleSubmit}
            initialValues={INITIAL_VALUES}
            // eslint-disable-next-line
            childrenStackSpacing={5}
            submitButtonLabel={t('loginButtonTitle')}
            enableDirtyFormHandling={false}
            submitButtonUseFullWidth
            disableSubmitIfInvalid={false}
            disableSubmitButton={isNativeAuthInProgress}
          >
            {manifestData?.enabled_languages &&
            manifestData.enabled_languages.length > 1 ? (
              <PsSelectTuner
                inputId="language-selector"
                label={language}
                name="language"
                value={prefLanguage}
                onPsChange={(event) => {
                  const target = event.target as HTMLElement | null;
                  const selectElement = target?.querySelector(
                    'select'
                  ) as HTMLSelectElement | null;

                  if (selectElement) {
                    const value = selectElement.value;
                    if (ACCEPTED_VALUES.includes(value)) {
                      setprefLanguage(value);
                    }
                  }
                }}
              >
                {options.map((option) => (
                  <option
                    key={option.key}
                    value={option.value}
                    lang={t(`common:lang_${option.value}`)}
                  >
                    {option.label}
                  </option>
                ))}
              </PsSelectTuner>
            ) : (
              <></>
            )}
            <TextField
              fullWidth
              id="username"
              label={t('username')}
              name="username"
              externalError={externalError}
              disabled={isNativeAuthInProgress}
              autocomplete="username"
            />
            <TextField
              fullWidth
              id="password"
              className="login-password-input"
              label={t('password')}
              name="password"
              type={showLoginPassword ? 'text' : 'password'}
              externalError={externalError}
              disabled={isNativeAuthInProgress}
              autocomplete="current-password"
              endAdornment={
                <button
                  onClick={handleClickShowPassword}
                  className="password-visibility-icon click-icon"
                  type="button"
                >
                  {showLoginPassword ? (
                    <>
                      <i
                        className="ph ph-eye-slash"
                        aria-hidden="true"
                        role="presentation"
                      ></i>{' '}
                      <span className="sr-only">{t('hidePassword')}</span>
                    </>
                  ) : (
                    <>
                      <i
                        className="ph ph-eye"
                        aria-hidden="true"
                        role="presentation"
                      ></i>{' '}
                      <span className="sr-only">{t('showPassword')}</span>
                    </>
                  )}
                </button>
              }
            />
            <>
              {isNativeAuthFlowEnabled && (
                <IconButton
                  testId="native-auth-button"
                  Icon={UserFocus}
                  onClick={authenticate}
                  size={3.6}
                  variant="tertiary"
                  isLoading={isNativeAuthInProgress}
                  disabled={isLoggingIn}
                  fullWidth
                />
              )}
            </>
          </Form>
          <Spacer height={8} />
          <Link
            testId="forgot-password-link"
            value={t('forgotPasswordLinkTitle')}
            path={ROUTES.public.forgotPassword.path}
            capitalize
            disabled={isNativeAuthInProgress}
            internal
          />
          <Spacer height={1} />
          <Link
            testId="legal-docs-link"
            value={t('legalDocs')}
            path={ROUTES.public.legalDocuments.path}
            capitalize
            disabled={isNativeAuthInProgress}
            internal
          />
          {!isMobile && <ChatBot renderDefault={false} useDesktopIcon={true} />}
        </main>
      </PaperStyled>
      {isMobile && <ChatBot renderDefault={false} useDesktopIcon={true} />}
    </LoginPageWrapperStyled>
  );
};
